import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function About() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About Parish Audio",
    url: "https://parishaudio.com/about",
    description:
      "Learn about Parish Audio, a founder-led audio software company built around real music-making, careful listening, and plugins designed for practical use.",
    mainEntity: {
      "@type": "Organization",
      name: "Parish Audio",
      url: "https://parishaudio.com",
      description:
        "Parish Audio creates focused audio plugins shaped by decades of hands-on music, recording, tone chasing, and practical real-world use.",
    },
  };

  const panelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel || "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel || "rgba(0,0,0,0.18)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="About | Parish Audio"
        description="Parish Audio creates focused audio plugins shaped by decades of music, recording, and hands-on listening, with a strong emphasis on sound quality and practical design."
        canonical="https://parishaudio.com/about"
        schema={aboutSchema}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Paper
                elevation={0}
                sx={{
                  ...panelSx,
                  p: { xs: 1.5, md: 2.25 },
                  backgroundColor: style.darkPanel || "rgba(255,255,255,0.04)",
                }}
              >
                <Stack spacing={1.25}>
                  <Chip
                    label="Founder-Led • Built from Real Use"
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: "5px",
                      color: style.color4,
                      fontWeight: 700,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  />

                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "2rem", md: "3rem" },
                      lineHeight: 1.04,
                      fontWeight: 800,
                      color: style.color4,
                      letterSpacing: "-0.03em",
                      maxWidth: 820,
                    }}
                  >
                    Plugins shaped by a lifetime of playing, recording, and chasing better sound
                  </Typography>

                  <Typography
                    sx={{
                      color: style.color3,
                      fontSize: { xs: "1rem", md: "1.05rem" },
                      lineHeight: 1.8,
                      maxWidth: 800,
                    }}
                  >
                    Parish Audio was built from the perspective of someone who has spent years
                    playing music, writing songs, recording, listening closely, and caring about
                    the details that make a sound feel right. The goal is simple: create focused
                    tools with strong sound quality, practical control, and a place in real sessions.
                  </Typography>
                </Stack>
              </Paper>

              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                    <Stack spacing={1.5}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          color: style.color4,
                        }}
                      >
                        The story behind Parish Audio
                      </Typography>

                      <Typography
                        sx={{
                          color: style.color4,
                          lineHeight: 1.8,
                          whiteSpace: "pre-line",
                          fontSize: "1rem",
                        }}
                      >
                        {`Music was part of life early on. I started playing piano at eight, and before long I was drawn into guitar, drums, bass, and anything else I could get my hands on. There were always instruments around the house. My dad is a strong lead guitar player with a deep love for progressive rock and metal, and watching him play became one of the earliest reasons music felt exciting to me. My mom is a great piano player, and hearing her sit down and play was just as much a part of that foundation.

Over time, that turned into a broader background in writing, recording, learning songs, playing in school groups, and starting bands with friends. Guitar became a major creative outlet, but bass, piano, and drums all shaped the way I hear arrangement, feel, and tone. That mix of instruments matters because it changed the way I listen. I do not think about audio tools from just one angle. I think about them as a player, songwriter, recorder, and someone who cares deeply about how a part actually lands in a track.

Years later, that same interest in sound led into programming and plugin development. Parish Audio started because I wanted to build the tools I was looking for myself, with no compromises in sound quality. I have a very discerning ear, and the products are shaped by that. The point is not to add features for the sake of it. The point is to make tools that sound right, feel right, and hold up in real use.

That is still what drives us now. Parish Audio is a focused line of plugins built around sound quality, usable control, and products that earn their place in a session. I make them because I want them in my own work, and I offer them because I think other musicians, producers, and engineers may find the same value in them.`}
                      </Typography>
                    </Stack>
                  </Paper>

                  <Stack spacing={2}>
                    <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                      <Stack spacing={1.25}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: style.color4,
                          }}
                        >
                          What guides us
                        </Typography>

                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.15 }}>
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.35 }}>
                            Sound quality first
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.65, fontSize: "0.94rem" }}>
                            The sound has to hold up before anything else. Every decision starts there.
                          </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.15 }}>
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.35 }}>
                            Practical control
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.65, fontSize: "0.94rem" }}>
                            The controls should lead to useful decisions quickly, without unnecessary clutter.
                          </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.15 }}>
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.35 }}>
                            Built from real use
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.65, fontSize: "0.94rem" }}>
                            These are tools shaped by actual playing, recording, mixing, and tone chasing.
                          </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.15 }}>
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.35 }}>
                            Focus over feature bloat
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.65, fontSize: "0.94rem" }}>
                            A plugin should earn its place by being genuinely useful, not just packed with options.
                          </Typography>
                        </Paper>
                      </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                      <Stack spacing={1.1}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: style.color4,
                          }}
                        >
                          Background
                        </Typography>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 1,
                          }}
                        >
                          {[
                            "Started on piano at age 8",
                            "Guitar and drums since the mid-1990s",
                            "Also played violin, bass, and school jazz band",
                            "Years of writing, recording, and band experience",
                            "Programming and plugin development since 2014",
                          ].map((item, idx) => (
                            <Paper
                              key={idx}
                              elevation={0}
                              sx={{
                                ...subtlePanelSx,
                                p: 1.05,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: style.color4,
                                  lineHeight: 1.5,
                                  fontSize: "0.93rem",
                                }}
                              >
                                {item}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Box>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Stack spacing={1.1}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          color: style.color4,
                        }}
                      >
                        What you can expect from Parish Audio
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Parish Audio is built for people who care about sound and want tools that
                        feel dependable, clear, and worth keeping in the session. The aim is to make
                        plugins that help you get where you want to go without slowing you down or
                        asking you to work around unnecessary complexity.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Whether the job is shaping a vocal, adding space, tightening noise, dialing
                        in drive, or bringing more life into a track, the goal stays the same:
                        strong sound, useful control, and products designed with real use in mind.
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Stack spacing={1.1}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          color: style.color4,
                        }}
                      >
                        Explore Parish Audio
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        Browse the plugin lineup, read through the Learning Center, or get in touch
                        if you have questions about products, compatibility, or support.
                      </Typography>

                      <Stack direction={{ xs: "column", sm: "row", md: "column" }} spacing={1.1}>
                        <Button
                          component={RouterLink}
                          to="/plugins"
                          sx={{
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor: style.accentGreen || style.color2,
                            "&:hover": {
                              backgroundColor: style.accentGreen || style.color2,
                              filter: "brightness(1.05)",
                            },
                          }}
                        >
                          Browse Plugins
                        </Button>

                        <Button
                          component={RouterLink}
                          to="/learning"
                          sx={{
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.10)",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.10)",
                            },
                          }}
                        >
                          Learning Center
                        </Button>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}